import React, { useState, useEffect } from 'react';
import api from '../api';

const PERMISSION_GROUPS = [
  {
    label: 'Sales CRM',
    keys: [
      { key: 'pipeline', label: 'Pipeline & Deals' },
      { key: 'crm_contacts', label: 'Contacts' },
      { key: 'outreach', label: 'Outreach & Campaigns' },
      { key: 'inbox', label: 'Inbox' },
    ]
  },
  {
    label: 'Talent CRM',
    keys: [
      { key: 'talent_network', label: 'Network' },
      { key: 'talent_lists', label: 'Lists & Forecasting' },
      { key: 'talent_metrics', label: 'Metrics & Performance' },
      { key: 'talent_reports', label: 'Campaign Reports' },
    ]
  },
  {
    label: 'Admin',
    keys: [
      { key: 'settings', label: 'Settings & User Management' },
    ]
  }
];

const ALL_KEYS = PERMISSION_GROUPS.flatMap(g => g.keys.map(k => k.key));

const PRESETS = {
  admin: ALL_KEYS.reduce((o, k) => ({...o, [k]: true}), {}),
  sales: { pipeline: true, crm_contacts: true, outreach: true, inbox: true, talent_network: false, talent_lists: false, talent_metrics: false, talent_reports: false, settings: false },
  talent: { pipeline: false, crm_contacts: false, outreach: false, inbox: false, talent_network: true, talent_lists: true, talent_metrics: true, talent_reports: true, settings: false },
  full_access: ALL_KEYS.reduce((o, k) => ({...o, [k]: k !== 'settings' ? true : false}), {}),
};

const TeamManagement = ({ user }) => {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editPerms, setEditPerms] = useState({});
  const [saving, setSaving] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [resettingPw, setResettingPw] = useState(null);

  const isAdmin = user?.org_role === 'admin';

  const fetchData = async () => {
    try {
      const [orgRes, invitesRes] = await Promise.all([
        api.get('/org'),
        api.get('/org/invites').catch(() => ({ data: { invites: [] } })),
      ]);
      setMembers(orgRes.data.members || []);
      setInvites(invitesRes.data.invites || []);
    } catch (e) {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const startEdit = (member) => {
    setEditingUser(member.user_id);
    const perms = member.permissions || {};
    setEditPerms(ALL_KEYS.reduce((o, k) => ({...o, [k]: !!perms[k]}), {}));
  };

  const applyPreset = (preset) => {
    setEditPerms({...PRESETS[preset]});
  };

  const savePermissions = async () => {
    setSaving(true);
    try {
      await api.put(`/org/member/${editingUser}/permissions`, { permissions: editPerms });
      setEditingUser(null);
      fetchData();
    } catch (e) { alert('Failed to save permissions'); }
    finally { setSaving(false); }
  };

  const sendInvite = async () => {
    if (!inviteEmail) return;
    try {
      const res = await api.post('/org/invite', { email: inviteEmail, role: 'member' });
      const token = res.data.invite?.token;
      if (token) setInviteLink(`${window.location.origin}/invite/${token}`);
      setInviteEmail('');
      fetchData();
    } catch (e) { alert(e.response?.data?.error || 'Failed to send invite'); }
  };

  const cancelInvite = async (id) => {
    try { await api.delete(`/org/invite/${id}`); fetchData(); }
    catch (e) { alert('Failed'); }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Remove this member from the organization?')) return;
    try { await api.delete(`/org/member/${userId}`); fetchData(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const resetPassword = async (userId) => {
    const pw = prompt('Enter new password for this user (min 6 chars):');
    if (!pw || pw.length < 6) { if (pw) alert('Password must be at least 6 characters'); return; }
    setResettingPw(userId);
    try {
      await api.post(`/org/member/${userId}/reset-password`, { new_password: pw });
      alert('Password reset successfully');
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setResettingPw(null); }
  };

  const getPermSummary = (perms) => {
    if (!perms || Object.keys(perms).length === 0) return 'No permissions set';
    const on = ALL_KEYS.filter(k => perms[k]);
    if (on.length === ALL_KEYS.length) return 'Full Admin';
    if (on.length === 0) return 'No access';
    // Check presets
    const salesKeys = ['pipeline', 'crm_contacts', 'outreach', 'inbox'];
    const talentKeys = ['talent_network', 'talent_lists', 'talent_metrics', 'talent_reports'];
    const hasSales = salesKeys.every(k => perms[k]);
    const hasTalent = talentKeys.every(k => perms[k]);
    const noSales = salesKeys.every(k => !perms[k]);
    const noTalent = talentKeys.every(k => !perms[k]);
    if (hasSales && noTalent && !perms.settings) return 'Sales Rep';
    if (hasTalent && noSales && !perms.settings) return 'Talent Manager';
    if (hasSales && hasTalent && !perms.settings) return 'Full Access';
    return `${on.length}/${ALL_KEYS.length} permissions`;
  };

  if (loading) return <div className="text-zinc-500 text-sm">Loading team...</div>;
  if (!isAdmin) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Team Management</h2>
          <p className="text-zinc-500 text-sm">Manage team members, permissions, and invitations</p>
        </div>
        <button onClick={() => setShowInvite(!showInvite)}
          className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold">
          {showInvite ? 'Cancel' : '+ Invite Member'}
        </button>
      </div>

      {/* Invite form */}
      {showInvite && (
        <div className="bg-zinc-800 rounded-lg p-4 mb-4">
          <div className="flex gap-2">
            <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && sendInvite()}
              placeholder="Email address..." className="flex-1 bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm" autoFocus />
            <button onClick={sendInvite} disabled={!inviteEmail}
              className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">Send Invite</button>
          </div>
          {inviteLink && (
            <div className="mt-3">
              <div className="text-zinc-400 text-xs mb-1">Or share this invite link:</div>
              <div className="flex gap-2">
                <input type="text" readOnly value={inviteLink} className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-zinc-300 text-xs font-mono" />
                <button onClick={() => { navigator.clipboard.writeText(inviteLink); }}
                  className="bg-zinc-700 text-white px-3 py-1.5 rounded text-xs">Copy</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending invites */}
      {invites.length > 0 && (
        <div className="mb-4">
          <div className="text-zinc-500 text-xs font-semibold uppercase tracking-wide mb-2">Pending Invites</div>
          {invites.map(inv => (
            <div key={inv.id} className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-4 py-2 mb-1">
              <div>
                <span className="text-zinc-300 text-sm">{inv.email}</span>
                <span className="text-zinc-600 text-xs ml-2">Expires {new Date(inv.expires_at).toLocaleDateString()}</span>
              </div>
              <button onClick={() => cancelInvite(inv.id)} className="text-zinc-600 hover:text-red-400 text-xs">Cancel</button>
            </div>
          ))}
        </div>
      )}

      {/* Members table */}
      <div className="space-y-2">
        {members.map(m => {
          const isEditing = editingUser === m.user_id;
          const isSelf = m.user_id === user?.id;
          const permSummary = getPermSummary(m.permissions);
          return (
            <div key={m.user_id} className={`bg-zinc-800 rounded-xl overflow-hidden ${isEditing ? 'border border-nextide-turquoise/30' : ''}`}>
              {/* Member row */}
              <div className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {(m.name || m.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-white text-sm font-medium truncate">
                      {m.name || m.email}
                      {isSelf && <span className="text-zinc-500 text-xs ml-2">(you)</span>}
                    </div>
                    <div className="text-zinc-500 text-xs truncate">{m.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    permSummary === 'Full Admin' ? 'bg-purple-500/20 text-purple-400' :
                    permSummary === 'Sales Rep' ? 'bg-blue-500/20 text-blue-400' :
                    permSummary === 'Talent Manager' ? 'bg-green-500/20 text-green-400' :
                    permSummary === 'Full Access' ? 'bg-nextide-turquoise/20 text-nextide-turquoise' :
                    'bg-zinc-700 text-zinc-400'
                  }`}>{permSummary}</span>
                  {!isSelf && (
                    <div className="flex gap-1">
                      <button onClick={() => isEditing ? setEditingUser(null) : startEdit(m)}
                        className={`text-xs px-2 py-1 rounded ${isEditing ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-nextide-turquoise'}`}>
                        {isEditing ? 'Close' : 'Permissions'}
                      </button>
                      <button onClick={() => resetPassword(m.user_id)} disabled={resettingPw === m.user_id}
                        className="text-xs text-zinc-500 hover:text-yellow-400 px-2 py-1">
                        {resettingPw === m.user_id ? '...' : 'Reset PW'}
                      </button>
                      <button onClick={() => removeMember(m.user_id)}
                        className="text-xs text-zinc-600 hover:text-red-400 px-2 py-1">Remove</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Permissions editor */}
              {isEditing && (
                <div className="border-t border-zinc-700 px-5 py-4">
                  {/* Preset buttons */}
                  <div className="flex gap-2 mb-4">
                    <span className="text-zinc-500 text-xs self-center mr-1">Quick assign:</span>
                    {[
                      { key: 'admin', label: 'Admin', cls: 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' },
                      { key: 'sales', label: 'Sales Rep', cls: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' },
                      { key: 'talent', label: 'Talent Manager', cls: 'bg-green-500/20 text-green-400 hover:bg-green-500/30' },
                      { key: 'full_access', label: 'Full Access', cls: 'bg-nextide-turquoise/20 text-nextide-turquoise hover:bg-nextide-turquoise/30' },
                    ].map(p => (
                      <button key={p.key} onClick={() => applyPreset(p.key)}
                        className={`px-3 py-1 rounded text-xs font-medium ${p.cls}`}>{p.label}</button>
                    ))}
                  </div>

                  {/* Permission checkboxes */}
                  <div className="grid grid-cols-3 gap-6">
                    {PERMISSION_GROUPS.map(group => (
                      <div key={group.label}>
                        <div className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-2">{group.label}</div>
                        <div className="space-y-2">
                          {group.keys.map(perm => (
                            <label key={perm.key} className="flex items-center gap-2 cursor-pointer group">
                              <input type="checkbox" checked={!!editPerms[perm.key]}
                                onChange={e => setEditPerms(prev => ({...prev, [perm.key]: e.target.checked}))}
                                className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-nextide-turquoise focus:ring-nextide-turquoise focus:ring-offset-0 cursor-pointer" />
                              <span className="text-sm text-zinc-300 group-hover:text-white">{perm.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Save */}
                  <div className="flex gap-2 mt-4 pt-3 border-t border-zinc-700">
                    <button onClick={savePermissions} disabled={saving}
                      className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                      {saving ? 'Saving...' : 'Save Permissions'}
                    </button>
                    <button onClick={() => setEditingUser(null)}
                      className="bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TeamManagement;
